import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileHeart, Save, Loader2, Phone, Droplets, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

export default function InfirmerieFichesPage() {
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState("");
  const [record, setRecord] = useState({
    student_id: "", blood_group: "", allergies: "", chronic_conditions: "",
    current_treatments: "", emergency_contact_name: "", emergency_contact_phone: "",
    doctor_name: "", doctor_phone: "", notes: "", vaccinations: [],
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/students").then(({ data }) => setStudents(data)).catch(() => toast.error("Erreur chargement élèves"));
  }, []);

  const loadRecord = async (sid) => {
    if (!sid) return;
    setSelected(sid);
    setLoading(true);
    try {
      const { data } = await api.get(`/infirmary/medical-records/${sid}`);
      setRecord({
        student_id: sid,
        blood_group: data.blood_group || "",
        allergies: data.allergies || "",
        chronic_conditions: data.chronic_conditions || "",
        current_treatments: data.current_treatments || "",
        emergency_contact_name: data.emergency_contact_name || "",
        emergency_contact_phone: data.emergency_contact_phone || "",
        doctor_name: data.doctor_name || "",
        doctor_phone: data.doctor_phone || "",
        notes: data.notes || "",
        vaccinations: data.vaccinations || [],
      });
    } catch {
      toast.error("Erreur");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!record.student_id) return toast.error("Sélectionnez un élève");
    setSaving(true);
    try {
      await api.post("/infirmary/medical-records", record);
      toast.success("Fiche médicale enregistrée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const student = students.find(s => s.id === selected);

  return (
    <div className="space-y-6" data-testid="fiches-page">
      <div>
        <div className="chip mb-2"><FileHeart className="h-3 w-3" /><span>FICHES MÉDICALES</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Fiches médicales des élèves</h1>
        <p className="text-slate-500 mt-1">Allergies, groupe sanguin, contacts d&apos;urgence — informations cruciales en cas d&apos;incident.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
        <Label>Sélectionnez un élève</Label>
        <Select value={selected} onValueChange={loadRecord}>
          <SelectTrigger className="max-w-xl mt-1" data-testid="select-student"><SelectValue placeholder="Chercher un élève..." /></SelectTrigger>
          <SelectContent className="max-h-96">
            {students.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} {s.class_name && `— ${s.class_name}`}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {selected && !loading && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-5" data-testid="fiche-form">
          {student && (
            <div className="bg-slate-50 p-4 rounded-xl">
              <div className="text-xs uppercase font-bold tracking-wider text-slate-500 mb-1">Élève</div>
              <div className="font-heading font-bold text-lg text-[#1E3A8A]">{student.first_name} {student.last_name}</div>
              <div className="text-sm text-slate-500">{student.class_name}</div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center gap-1"><Droplets className="h-4 w-4 text-red-500" /> Groupe sanguin</Label>
              <Select value={record.blood_group} onValueChange={(v) => setRecord({...record, blood_group: v})}>
                <SelectTrigger data-testid="blood-group"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{BLOOD_GROUPS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="flex items-center gap-1"><AlertCircle className="h-4 w-4 text-orange-500" /> Allergies connues</Label>
              <Input value={record.allergies} onChange={e => setRecord({...record, allergies: e.target.value})} placeholder="Arachide, pénicilline, lait..." data-testid="allergies" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Maladies chroniques</Label>
              <Input value={record.chronic_conditions} onChange={e => setRecord({...record, chronic_conditions: e.target.value})} placeholder="Asthme, diabète, drépanocytose..." data-testid="chronic" />
            </div>
            <div>
              <Label>Traitements en cours</Label>
              <Input value={record.current_treatments} onChange={e => setRecord({...record, current_treatments: e.target.value})} placeholder="Ventoline matin/soir" data-testid="treatments" />
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <h3 className="font-heading font-bold text-lg text-[#1E3A8A] mb-3 flex items-center gap-2"><Phone className="h-5 w-5" /> Contacts d&apos;urgence</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nom du contact d&apos;urgence</Label>
                <Input value={record.emergency_contact_name} onChange={e => setRecord({...record, emergency_contact_name: e.target.value})} placeholder="Mme Diallo Aïcha" data-testid="emergency-name" />
              </div>
              <div>
                <Label>Téléphone urgence</Label>
                <Input value={record.emergency_contact_phone} onChange={e => setRecord({...record, emergency_contact_phone: e.target.value})} placeholder="+224 622 00 00 00" data-testid="emergency-phone" />
              </div>
              <div>
                <Label>Médecin traitant</Label>
                <Input value={record.doctor_name} onChange={e => setRecord({...record, doctor_name: e.target.value})} placeholder="Dr Camara" data-testid="doctor-name" />
              </div>
              <div>
                <Label>Téléphone médecin</Label>
                <Input value={record.doctor_phone} onChange={e => setRecord({...record, doctor_phone: e.target.value})} placeholder="+224 622 11 22 33" data-testid="doctor-phone" />
              </div>
            </div>
          </div>

          <div>
            <Label>Notes complémentaires</Label>
            <Textarea value={record.notes} onChange={e => setRecord({...record, notes: e.target.value})} rows={3} placeholder="Particularités, comportements en cas de crise, etc." data-testid="notes" />
          </div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white" data-testid="save-fiche">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}Enregistrer la fiche
            </Button>
          </div>
        </div>
      )}
      {loading && <div className="text-center py-8 text-slate-400">Chargement...</div>}
    </div>
  );
}
